import { useState, createContext, useContext, useEffect, type ReactNode } from 'react';

import { useGetCountryControllerQuery } from 'services/talentoAPI/hiringAPI';

const externalCodeContext = createContext({
  countryOfResidence: '',
  departmentOfResidence: '',
  cityOfResidence: '',
  countryOfBirth: '',
  departmentOfBirth: '',
  countryStatesCode: '',
  countryCitiesCode: '',
  countryBirthStatesCode: '',
  countryBirthCitiesCode: '',
  isErrorCountryController: false,
  isErrorCountryBirthController: false,
  setCountryOfResidence: (country: string) => {},
  setDepartmentOfResidence: (department: string) => {},
  setCityOfResidence: (city: string) => {},
  setCountryOfBirth: (country: string) => {},
  setDepartmentOfBirth: (department: string) => {},
  setCountryStatesCode: (code: string) => {},
  setCountryCitiesCode: (code: string) => {},
  setCountryBirthStatesCode: (code: string) => {},
  setCountryBirthCitiesCode: (code: string) => {},
});

export function useExternalCodeContext() {
  return useContext(externalCodeContext);
}

export function ExternalCodesProvider({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  const [countryOfResidence, setCountryOfResidence] = useState('');
  const [departmentOfResidence, setDepartmentOfResidence] = useState('');
  const [cityOfResidence, setCityOfResidence] = useState('');
  const [countryStatesCode, setCountryStatesCode] = useState('');
  const [countryCitiesCode, setCountryCitiesCode] = useState('');
  const [countryBirthStatesCode, setCountryBirthStatesCode] = useState('');
  const [countryBirthCitiesCode, setCountryBirthCitiesCode] = useState('');
  const [countryOfBirth, setCountryOfBirth] = useState('');
  const [departmentOfBirth, setDepartmentOfBirth] = useState('');

  const {
    refetch,
    data,
    isLoading,
    isError: isErrorCountryController,
    isFetching,
  } = useGetCountryControllerQuery(countryOfResidence);

  const {
    refetch: refetchCountryBirth,
    data: dataCountryBirth,
    isLoading: isLoadingCountryBirth,
    isError: isErrorCountryBirthController,
    isFetching: isFetchingCountryBirth,
  } = useGetCountryControllerQuery(countryOfBirth);

  useEffect(() => {
    if (!(isLoading || isFetching)) {
      let departmentListCode = '';
      let cityListCode = '';

      if (!isErrorCountryController) {
        departmentListCode = data?.departmentList ?? '';
        cityListCode = data?.cityList ?? '';
      }

      setCountryStatesCode(departmentListCode);
      setCountryCitiesCode(cityListCode);
    }
  }, [data, isLoading, isErrorCountryController, isFetching]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line
  }, [countryOfResidence]);

  useEffect(() => {
    if (!(isLoadingCountryBirth || isFetchingCountryBirth)) {
      let departmentListCode = '';
      let cityListCode = '';

      if (!isErrorCountryBirthController) {
        departmentListCode = dataCountryBirth?.departmentList ?? '';
        cityListCode = dataCountryBirth?.cityList ?? '';
      }

      setCountryBirthStatesCode(departmentListCode);
      setCountryBirthCitiesCode(cityListCode);
    }
  }, [
    dataCountryBirth,
    isLoadingCountryBirth,
    isErrorCountryBirthController,
    isFetchingCountryBirth,
  ]);

  useEffect(() => {
    refetchCountryBirth();
    // eslint-disable-next-line
  }, [countryOfBirth]);

  const value = {
    countryOfResidence,
    departmentOfResidence,
    cityOfResidence,
    countryOfBirth,
    departmentOfBirth,
    countryStatesCode,
    countryCitiesCode,
    countryBirthStatesCode,
    countryBirthCitiesCode,
    isErrorCountryController,
    isErrorCountryBirthController,
    setCountryOfResidence,
    setDepartmentOfResidence,
    setCityOfResidence,
    setCountryOfBirth,
    setDepartmentOfBirth,
    setCountryStatesCode,
    setCountryCitiesCode,
    setCountryBirthStatesCode,
    setCountryBirthCitiesCode,
  };

  return <externalCodeContext.Provider value={value}>{children}</externalCodeContext.Provider>;
}
